/**
 * Arabic names for the curated city list in `country-data.ts`.
 *
 * Same contract as `country-names.ts`: the canonical English
 * `${city}, ${country}` string stays the stored value and the value sent to
 * the API (job `location` / `city` / `country` columns hold English text, so
 * an Arabic value would match nothing). Arabic exists only as a rendered
 * label and as a search alias.
 *
 * Keys are `${ISO alpha-2}:${English city}` because city names are not unique
 * across countries (Tripoli, Alexandria, Cambridge...).
 */
import { LOCATION_META } from './country-data';
import { localizedCountryName, normalizeSearchText } from './country-names';
import type { SupportedLocale } from '../hooks/useLocale';

const AR_CITY_NAMES: Record<string, string> = {
  // GCC
  'AE:Abu Dhabi': 'أبوظبي',
  'AE:Dubai': 'دبي',
  'AE:Sharjah': 'الشارقة',
  'AE:Ajman': 'عجمان',
  'AE:Ras Al Khaimah': 'رأس الخيمة',
  'AE:Fujairah': 'الفجيرة',
  'AE:Al Ain': 'العين',
  'SA:Riyadh': 'الرياض',
  'SA:Jeddah': 'جدة',
  'SA:Dammam': 'الدمام',
  'SA:Makkah': 'مكة المكرمة',
  'SA:Madinah': 'المدينة المنورة',
  'SA:Khobar': 'الخبر',
  'QA:Doha': 'الدوحة',
  'QA:Al Wakrah': 'الوكرة',
  'QA:Al Rayyan': 'الريان',
  'KW:Kuwait City': 'مدينة الكويت',
  'KW:Hawalli': 'حولي',
  'KW:Salmiya': 'السالمية',
  'BH:Manama': 'المنامة',
  'BH:Muharraq': 'المحرق',
  'BH:Riffa': 'الرفاع',
  'OM:Muscat': 'مسقط',
  'OM:Salalah': 'صلالة',
  'OM:Sohar': 'صحار',
  // Middle East
  'JO:Amman': 'عمّان',
  'JO:Irbid': 'إربد',
  'JO:Zarqa': 'الزرقاء',
  'LB:Beirut': 'بيروت',
  'LB:Tripoli': 'طرابلس',
  'LB:Sidon': 'صيدا',
  'EG:Cairo': 'القاهرة',
  'EG:Alexandria': 'الإسكندرية',
  'EG:Giza': 'الجيزة',
  'EG:Sharm El Sheikh': 'شرم الشيخ',
  'IQ:Baghdad': 'بغداد',
  'IQ:Erbil': 'أربيل',
  'IQ:Basra': 'البصرة',
  'IQ:Sulaymaniyah': 'السليمانية',
  // South Asia
  'IN:Mumbai': 'مومباي',
  'IN:Delhi': 'دلهي',
  'IN:Bangalore': 'بنغالور',
  'IN:Hyderabad': 'حيدر آباد',
  'IN:Chennai': 'تشيناي',
  'IN:Pune': 'بيون',
  'IN:Kolkata': 'كولكاتا',
  'IN:Ahmedabad': 'أحمد آباد',
  'PK:Karachi': 'كراتشي',
  'PK:Lahore': 'لاهور',
  'PK:Islamabad': 'إسلام آباد',
  'PK:Rawalpindi': 'روالبندي',
  'PK:Faisalabad': 'فيصل آباد',
  'PK:Peshawar': 'بيشاور',
  'BD:Dhaka': 'دكا',
  'BD:Chittagong': 'شيتاغونغ',
  'BD:Sylhet': 'سيلهيت',
  'BD:Khulna': 'خولنا',
  // Southeast Asia
  'PH:Manila': 'مانيلا',
  'PH:Cebu City': 'مدينة سيبو',
  'PH:Davao': 'دافاو',
  'PH:Quezon City': 'مدينة كويزون',
  'MY:Kuala Lumpur': 'كوالالمبور',
  'MY:Penang': 'بينانغ',
  'MY:Johor Bahru': 'جوهور بهرو',
  'MY:Kota Kinabalu': 'كوتا كينابالو',
  'SG:Singapore': 'سنغافورة',
  'TH:Bangkok': 'بانكوك',
  'TH:Chiang Mai': 'شيانغ ماي',
  'TH:Phuket': 'بوكيت',
  // East Asia
  'CN:Beijing': 'بكين',
  'CN:Shanghai': 'شنغهاي',
  'CN:Shenzhen': 'شنجن',
  'CN:Guangzhou': 'قوانغتشو',
  'CN:Hangzhou': 'هانغتشو',
  'JP:Tokyo': 'طوكيو',
  'JP:Osaka': 'أوساكا',
  'JP:Yokohama': 'يوكوهاما',
  'JP:Nagoya': 'ناغويا',
  'JP:Kyoto': 'كيوتو',
  'KR:Seoul': 'سول',
  'KR:Busan': 'بوسان',
  'KR:Incheon': 'إنتشون',
  // Europe
  'GB:London': 'لندن',
  'GB:Manchester': 'مانشستر',
  'GB:Birmingham': 'برمنغهام',
  'GB:Edinburgh': 'إدنبرة',
  'GB:Leeds': 'ليدز',
  'GB:Glasgow': 'غلاسكو',
  'DE:Berlin': 'برلين',
  'DE:Munich': 'ميونخ',
  'DE:Frankfurt': 'فرانكفورت',
  'DE:Hamburg': 'هامبورغ',
  'DE:Cologne': 'كولونيا',
  'FR:Paris': 'باريس',
  'FR:Lyon': 'ليون',
  'FR:Marseille': 'مرسيليا',
  'FR:Toulouse': 'تولوز',
  'FR:Nice': 'نيس',
  'NL:Amsterdam': 'أمستردام',
  'NL:Rotterdam': 'روتردام',
  'NL:The Hague': 'لاهاي',
  'NL:Utrecht': 'أوترخت',
  'IT:Rome': 'روما',
  'IT:Milan': 'ميلانو',
  'IT:Naples': 'نابولي',
  'IT:Turin': 'تورينو',
  'IT:Florence': 'فلورنسا',
  'ES:Madrid': 'مدريد',
  'ES:Barcelona': 'برشلونة',
  'ES:Valencia': 'فالنسيا',
  'ES:Seville': 'إشبيلية',
  // Americas
  'US:New York': 'نيويورك',
  'US:Los Angeles': 'لوس أنجلوس',
  'US:Chicago': 'شيكاغو',
  'US:Houston': 'هيوستن',
  'US:San Francisco': 'سان فرانسيسكو',
  'US:Seattle': 'سياتل',
  'US:Miami': 'ميامي',
  'US:Boston': 'بوسطن',
  'US:Washington DC': 'واشنطن العاصمة',
  'US:Austin': 'أوستن',
  'CA:Toronto': 'تورونتو',
  'CA:Vancouver': 'فانكوفر',
  'CA:Montreal': 'مونتريال',
  'CA:Calgary': 'كالغاري',
  'CA:Ottawa': 'أوتاوا',
  'BR:Sao Paulo': 'ساو باولو',
  'BR:Rio de Janeiro': 'ريو دي جانيرو',
  'BR:Brasilia': 'برازيليا',
  'MX:Mexico City': 'مدينة مكسيكو',
  'MX:Guadalajara': 'غوادالاخارا',
  'MX:Monterrey': 'مونتيري',
  // Africa
  'ZA:Johannesburg': 'جوهانسبرغ',
  'ZA:Cape Town': 'كيب تاون',
  'ZA:Durban': 'ديربان',
  'ZA:Pretoria': 'بريتوريا',
  'NG:Lagos': 'لاغوس',
  'NG:Abuja': 'أبوجا',
  'NG:Port Harcourt': 'بورت هاركورت',
  'KE:Nairobi': 'نيروبي',
  'KE:Mombasa': 'مومباسا',
  // Oceania
  'AU:Sydney': 'سيدني',
  'AU:Melbourne': 'ملبورن',
  'AU:Brisbane': 'بريسبان',
  'AU:Perth': 'بيرث',
  'AU:Adelaide': 'أديلايد',
  'NZ:Auckland': 'أوكلاند',
  'NZ:Wellington': 'ويلينغتون',
  'NZ:Christchurch': 'كرايستشيرش',
};

// U+060C ARABIC COMMA: an ASCII comma between two Arabic runs mirrors badly
// and reads as a Latin artefact in an otherwise Arabic chip.
const AR_SEPARATOR = '، ';

const arabicLabelCache = new Map<string, string | null>();

/**
 * Arabic `${city}، ${country}` for a canonical location value, or null when
 * either half has no Arabic name. Null means "render the English string as-is":
 * a half-translated label mixes scripts inside one chip and reads worse than
 * leaving it in English.
 */
export function arabicLocationLabel(value: string): string | null {
  const cached = arabicLabelCache.get(value);
  if (cached !== undefined) return cached;

  const resolve = (): string | null => {
    const meta = LOCATION_META?.[value];
    if (!meta) return null;
    const city = AR_CITY_NAMES[`${meta.countryCode}:${meta.city}`];
    if (!city) return null;
    const country = localizedCountryName(meta.countryCode, 'ar');
    // localizedCountryName falls back to the English name (then the ISO code)
    // when the Arabic dataset has no entry, so compare to detect that miss.
    if (!country || country === localizedCountryName(meta.countryCode, 'en')) {
      return null;
    }
    return `${city}${AR_SEPARATOR}${country}`;
  };

  const label = resolve();
  arabicLabelCache.set(value, label);
  return label;
}

/** Localized label for a canonical location value; English is the fallback. */
export function localizedLocationLabel(
  value: string,
  locale: SupportedLocale,
): string {
  if (locale !== 'ar') return value;
  return arabicLocationLabel(value) ?? value;
}

// Unicode script property rather than a literal character range, so no range
// in source can silently span the Arabic base-letter block (same reasoning as
// the \u escapes in country-names.ts).
const ARABIC_SCRIPT = /\p{Script=Arabic}/u;

/** True when the text contains Arabic-script characters. */
export function containsArabicScript(value: string): boolean {
  return ARABIC_SCRIPT.test(value ?? '');
}

/**
 * Normalized haystack for a canonical location: English and Arabic together,
 * so typing in either language finds the same chip regardless of UI locale.
 */
export function locationSearchText(value: string): string {
  const arabic = arabicLocationLabel(value);
  return normalizeSearchText(arabic ? `${value} ${arabic}` : value);
}
